import React, { FC, useMemo } from 'react';
import { Document, Font, Image, Link, Page, StyleSheet, Text, View } from '@react-pdf/renderer';
import {
  Education,
  ProfessionalExperience,
  References,
  VolunteerExperience,
} from '../../../models/UserModels';
import moment from 'moment';
import { PdfHtml } from '../PdfHtml';
import Utils from '../../../utils/Utils';
import { CvTemplateProfile, ProfileActivity } from '../CvTemplateModal';
import config from '../../../config';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
Font.register(Utils.getUbuntuFont());
Font.registerHyphenationCallback((word: string) => {
  const urlIndex = word.indexOf('://');
  if (urlIndex !== -1) {
    const schema = word.substring(0, urlIndex + 3);
    const urlText = word
      .substring(urlIndex + 3)
      .split('/')
      .map((v, i) => (i === 0 ? `${schema}${v}` : v));
    return urlText
      .map((v, i) => (i === urlText.length - 1 ? v : `${v}/`))
      .flatMap((v) => v.match(/(.{1,18})/g))
      .filter((v) => !!v) as string[];
  }
  return (word.match(/(.{1,18})/g) as string[]).filter((v) => !!v) as string[];
});
const colors = (color: string) => {
  let color1;
  let color2;
  let color3;
  if (color === '#14ae9f') {
    color1 = '#303b45';
    color2 = '#979ea1';
    color3 = '#1c2229';
  }
  if (color === '#468f9a') {
    color1 = '#303b45';
    color2 = '#979ea1';
    color3 = '#1c2229';
  }
  if (color === '#9dd9b8') {
    color1 = '#303b45';
    color2 = '#979ea1';
    color3 = '#1c2229';
  }
  if (color === '#38b8ba') {
    color1 = '#303b45';
    color2 = '#979ea1';
    color3 = '#1c2229';
  }
  if (color === '#006666') {
    color1 = '#303b45';
    color2 = '#979ea1';
    color3 = '#1c2229';
  }
  if (color === '#49308b') {
    color1 = '#303b45';
    color2 = '#979ea1';
    color3 = '#1c2229';
  }
  if (color === '#0fa8a8') {
    color1 = '#303b45';
    color2 = '#979ea1';
    color3 = '#1c2229';
  }
  if (color === '#815de3') {
    color1 = '#303b45';
    color2 = '#979ea1';
    color3 = '#1c2229';
  }
  return { color1, color2, color3 };
};
const pageStyles = (props?: any) =>
  StyleSheet.create({
    body: {
      // flex: 1,
      // flexDirection: 'row',
      fontFamily: 'Ubuntu',
      paddingBottom: 24,
    },
    contentWrapper: {
      flex: 1,
      flexDirection: 'column',
    },
    iconText: {
      fontSize: 9,
      fontWeight: 'light',
      fontStyle: 'italic',
    },
    header: {
      width: '100%',
      padding: 24,
      backgroundColor: props?.color ? colors(props.color).color1 : '#303b45',
    },
    headerName: {
      color: '#ffffff',
      fontSize: 24,
      fontWeight: 'normal',
    },
    headerStatement: {
      color: props?.color ? props.color : '#53b09f',
      fontSize: 13,
      fontWeight: 'normal',
      marginTop: 4,
    },
    summary: {
      color: '#ffffff',
      marginTop: 10,
    },
    contacts: {
      paddingVertical: 6,
      paddingHorizontal: 24,
      width: '100%',
      flexDirection: 'row',
      flexWrap: 'wrap',
      fontWeight: 'normal',
      justifyContent: 'center',
      fontSize: 8,
      backgroundColor: props?.color ? colors(props.color).color3 : '#1c2229',
    },
    row: {
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
      paddingHorizontal: 6,
    },
    durationCountryRow: {
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
    },
    headerContactText: {
      fontSize: 9,
      marginLeft: 5,
      color: '#ffffff',
      textDecoration: 'none',
    },

    resumeContent: {
      padding: 24,
    },

    titleText: {
      fontSize: 14,
      fontWeight: 'medium',
      color: props?.color ? props.color : '#1A1A1A',
      borderBottomWidth: 2,
      borderBottomColor: props?.color ? props?.color : '#6F6F6F',
      paddingHorizontal: 2,
    },
    designation: {
      fontSize: 12,
      fontWeight: 'medium',
      color: props?.color ? colors(props.color).color1 : '#000000',
    },
    company: {
      fontSize: 12,
      marginTop: 1,
      fontWeight: 'normal',
      color: props?.color ? colors(props.color).color1 : '#000000',
    },
    durationCountryWrapper: {
      flexDirection: 'row',
      width: '100%',
      justifyContent: 'space-between',
      marginTop: 4,
      marginBottom: 4,
      gap: 15,
    },
    country: {
      fontSize: 9,
      fontWeight: 'normal',
      color: props?.color ? colors(props.color).color2 : '#6F6F6F',
      marginLeft: 10,
      fontStyle: 'italic',
    },
    duration: {
      fontSize: 9,
      fontWeight: 'normal',
      color: props?.color ? colors(props.color).color2 : '#6F6F6F',
      fontStyle: 'italic',
    },
    description: {
      fontSize: 8,
      fontWeight: 'normal',
      color: props?.color ? props.color : '#6F6F6F',
    },
    dottedLine: {
      marginTop: 10,
      borderBottomWidth: 1,
      borderBottomColor: props?.color ? colors(props.color).color2 : '#E0E0E0',
      borderStyle: 'dashed',
    },
    resultsWrapper: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 5,
    },
    results: {
      fontSize: 10,
      fontWeight: 'medium',
      color: props?.color ? colors(props.color).color1 : '#6F6F6F',
    },
    languages: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 30,
    },
    skillWrapper: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      columnGap: 5,
      rowGap: 4,
      marginTop: 10,
    },
    skills: {
      fontSize: 10,
      paddingHorizontal: 6,
      paddingVertical: 3,
      borderRadius: 3,
      backgroundColor: props?.color ? colors(props.color).color2 : '#979ea1',
      fontWeight: 'normal',
      color: '#ffffff',
    },
    refName: {
      fontSize: 12,
      fontWeight: 'medium',
      color: props?.color ? colors(props.color).color1 : '#1A1A1A',
    },
    refDesignation: {
      fontSize: 12,
      marginTop: 2,
      fontWeight: 'normal',
      color: props?.color ? colors(props.color).color1 : '#6F6F6F',
    },
    refOrganization: {
      fontSize: 10,
      fontWeight: 'normal',
      color: props?.color ? colors(props.color).color1 : '#6F6F6F',
    },
    refContact: {
      flexDirection: 'column',
      alignItems: 'flex-start',
      marginTop: 2,
      marginBottom: 4,
      gap: 2,
    },
    refContactText: {
      fontSize: 10,
      marginTop: 1,
      fontWeight: 'normal',
      color: '#5F5F5F',
    },
  });

// Create Document Component
type Props = {
  selectedColor?: string;
  profile: CvTemplateProfile;
};
const ExpCard: FC<{ value: ProfessionalExperience; selectedColor?: string; isLast?: boolean }> = ({
  value,
  selectedColor,
  isLast,
}) => {
  const styles = useMemo(() => pageStyles({ color: selectedColor }), [selectedColor]);
  const duration = useMemo(() => {
    const from = moment(value.period.from);
    const fromStr = from.format('MMM YYYY');
    const to = moment(value.period.to);
    const toStr = value.period.to ? to.format('MMM YYYY') : 'Present';
    return `${fromStr} - ${toStr}`;
  }, [value.period.from, value.period.to]);
  return (
    <View wrap={false} style={{ marginTop: 10 }}>
      <Text style={styles.designation}>{value.designation.value}</Text>
      <Text style={styles.company}>{value.company.value}</Text>
      <View style={styles.durationCountryWrapper}>
        <IconTextView
          text={duration}
          img={`${config.frontendUrl}/calendarLine.png`}
          selectedColor={selectedColor}
          isIcon={false}
        />
        <IconTextView
          text={value.country || ''}
          img={`${config.frontendUrl}/locationLine.png`}
          selectedColor={selectedColor}
          isIcon={false}
        />
      </View>
      {value.description && <DescriptionText text={value.description} />}
      {!isLast && <Text />}
    </View>
  );
};
const VolCard: FC<{ value: VolunteerExperience; selectedColor?: string; isLast?: boolean }> = ({
  value,
  selectedColor,
  isLast,
}) => {
  const styles = useMemo(() => pageStyles({ color: selectedColor }), [selectedColor]);
  const duration = useMemo(() => {
    const from = moment(value.period.from);
    const fromStr = from.format('MMM YYYY');
    const to = moment(value.period.to);
    const toStr = value.period.to ? to.format('MMM YYYY') : 'Present';
    return `${fromStr} - ${toStr}`;
  }, [value.period.from, value.period.to]);
  return (
    <View wrap={false} style={{ marginTop: 10 }}>
      <Text style={styles.designation}>{value.organization.value}</Text>
      <Text style={styles.company}>{value.role.value}</Text>
      <View style={styles.durationCountryWrapper}>
        <IconTextView
          text={duration}
          img={`${config.frontendUrl}/calendarLine.png`}
          selectedColor={selectedColor}
          isIcon={false}
        />
      </View>
      {value.description && <DescriptionText text={value.description} />}
      {!isLast && <Text />}
    </View>
  );
};
const EduCard: FC<{ value: Education; selectedColor?: string; isLast?: boolean }> = ({
  value,
  selectedColor,
  isLast,
}) => {
  const styles = useMemo(() => pageStyles({ color: selectedColor }), [selectedColor]);
  const { type, results, institute, achievements, title, description } = value;
  const duration = useMemo(() => {
    const from = moment(value.period?.from);
    const fromStr = from.format('MMM YYYY');
    const to = moment(value.period?.to);
    const toStr = value?.period?.to
      ? to.format('MMM YYYY')
      : type === 'Certification'
      ? 'None'
      : 'Present';
    return [`${fromStr} - ${toStr}`, fromStr, toStr];
  }, [type, value?.period?.from, value?.period?.to]);
  return (
    <View wrap={false} style={{ marginTop: 10 }}>
      <View>
        {type === 'Certification' ? (
          <>
            <Text style={styles.designation}>{`${results[0]?.subject?.value ?? type}`}</Text>
          </>
        ) : type === 'GCE A/L' || type === 'GCE O/L' ? (
          <>
            <Text style={styles.designation}>{type}</Text>
            <View style={styles.resultsWrapper}>
              {results.map((r, i) => (
                <Text key={i} style={styles.results}>{`${r.subject?.value} ${
                  r.result ? `(${r.result}),` : ','
                }`}</Text>
              ))}
            </View>
          </>
        ) : type === 'Other' ? (
          <>
            <Text style={styles.designation}>{title ?? ''}</Text>
            <View style={styles.resultsWrapper}>
              {results.map((r, i) => (
                <Text key={i} style={styles.results}>{`${r.subject?.value} ${
                  r.result ? `(${r.result})` : ''
                }`}</Text>
              ))}
            </View>
          </>
        ) : (
          results.map((r, i) => (
            <Text key={i} style={styles.designation}>{`${r.subject?.value}${
              r.result ? `, ${r.result}` : ''
            }`}</Text>
          ))
        )}
      </View>
      {institute && <Text style={styles.company}>{institute.value}</Text>}
      {type !== 'Certification' && (
        <View style={styles.durationCountryWrapper}>
          <IconTextView
            text={duration[0]}
            img={`${config.frontendUrl}/calendarLine.png`}
            selectedColor={selectedColor}
            isIcon={false}
          />
          <IconTextView
            text={value.country || ''}
            img={`${config.frontendUrl}/locationLine.png`}
            selectedColor={selectedColor}
            isIcon={false}
          />
        </View>
      )}
      {type === 'Certification' && value?.period && (
        <View style={styles.durationCountryWrapper}>
          <IconTextView
            text={duration[1]}
            img={`${config.frontendUrl}/calendarLine.png`}
            selectedColor={selectedColor}
            isIcon={false}
          />
          <IconTextView
            text={value.country || ''}
            img={`${config.frontendUrl}/locationLine.png`}
            selectedColor={selectedColor}
            isIcon={false}
          />
        </View>
      )}
      {description && type === 'Other' && <DescriptionText text={description} />}
      {achievements && (
        <View style={styles.description}>
          <PdfHtml level={2} fontFamily="Ubuntu">
            {achievements.replace(/<li><br><\/li>/g, '').replace(/<p><br><\/p>/g, '')}
          </PdfHtml>
        </View>
      )}
      {!isLast && <Text />}
    </View>
  );
};
const ActivitySocityView: FC<{
  value: ProfileActivity[];
  selectedColor?: string;
}> = ({ value, selectedColor }) => {
  const styles = useMemo(() => pageStyles({ color: selectedColor }), [selectedColor]);
  const activity = value ?? [];
  const data = activity.map((item, index) => {
    const isLastActivity = index === activity.length - 1;
    return (
      <View wrap={false} key={index}>
        {index === 0 && (
          <Text style={[styles.designation, { marginTop: 10 }]}>Activities & Societies</Text>
        )}
        {item.institute && item.description && (
          <Text style={[styles.company, { marginTop: 6 }]}>{item.institute}</Text>
        )}

        {item.description.map((v) => {
          if (v) {
            return (
              <View wrap={false} style={{ marginTop: 4 }} key={v}>
                <DescriptionText text={v} />
              </View>
            );
          }
        })}
        {!isLastActivity && <Text />}
      </View>
    );
  });
  return <>{data}</>;
};
const ContactDetailView: FC<{
  img: string;
  value: string;
  selectedColor?: string;
  isUrl?: boolean;
  title?: string;
  type?: 'email';
  iconWidth?: number;
  iconHeight?: number;
  width?: string;
}> = ({ selectedColor, img, value, isUrl, title, type, iconHeight, iconWidth, width }) => {
  const styles = useMemo(() => pageStyles({ color: selectedColor }), [selectedColor]);
  return (
    <View style={[styles.row, { width: width }]}>
      <Image
        src={img}
        style={{
          width: iconWidth ?? 11,
          height: iconHeight ?? 11,
          color: selectedColor,
        }}
      />
      <View style={{ paddingHorizontal: 2, paddingVertical: 2 }}>
        {isUrl ? (
          <Link wrap style={styles.headerContactText} src={value}>
            {title ?? value}
          </Link>
        ) : type && type === 'email' ? (
          <Link wrap style={styles.headerContactText} src={value}>
            {title ?? value}
          </Link>
        ) : (
          <Text style={styles.headerContactText}>{value}</Text>
        )}
      </View>
    </View>
  );
};
const ReferenceView: FC<{
  value: References;
  selectedColor?: string;
  isLast?: boolean;
}> = ({ value, selectedColor, isLast }) => {
  const styles = useMemo(() => pageStyles({ color: selectedColor }), [selectedColor]);
  return (
    <View wrap={false} style={{ marginTop: 10 }}>
      <Text style={styles.refName}>
        {value.firstName} {value.lastName}
      </Text>
      <Text style={styles.refDesignation}>{value.designation}</Text>
      <Text style={styles.refOrganization}>{value.organization}</Text>
      <View style={styles.refContact}>
        <Text style={styles.refContactText}>{value.email}</Text>
        <Text style={styles.refContactText}>{value.mobile}</Text>
      </View>
      {!isLast && <Text />}
    </View>
  );
};

const TitleText: FC<{ title: string; selectedColor?: string }> = ({ title, selectedColor }) => {
  const styles = useMemo(() => pageStyles({ color: selectedColor }), [selectedColor]);
  return (
    <View style={{ flexDirection: 'row' }}>
      <Text style={styles.titleText}>{title.toUpperCase()}</Text>
    </View>
  );
};

const DescriptionText: FC<{
  text: string;
  color?: string;
  fontWeight?: string;
  lineHeight?: number;
  fontSize?: string;
}> = ({ text, color, fontWeight, lineHeight, fontSize }) => {
  return (
    <PdfHtml
      level={2}
      fontSize={fontSize ?? '10'}
      textColor={color}
      fontFamily="Ubuntu"
      fontWeight={fontWeight}
      lineHeight={lineHeight}>
      {text.replace(/<li><br><\/li>/g, '').replace(/<p><br><\/p>/g, '')}
    </PdfHtml>
  );
};
const IconTextView: FC<{
  text: string;
  img: string;
  isIcon: boolean;
  selectedColor?: string;
  iconWidth?: number;
  iconHeight?: number;
}> = ({ text, img, selectedColor, iconWidth, iconHeight, isIcon }) => {
  const styles = useMemo(() => pageStyles({ color: selectedColor }), [selectedColor]);
  return (
    <View style={styles.durationCountryRow}>
      {isIcon && (
        <Image
          src={img}
          style={{
            width: iconWidth ?? 10,
            height: iconHeight ?? 10,
            marginRight: 2,
            color: selectedColor,
            padding: 1,
          }}
        />
      )}
      <Text style={[styles.iconText, { color: selectedColor }]}>{text}</Text>
    </View>
  );
};

const Template2 = (props: Props) => {
  let newColor = props.selectedColor ? props.selectedColor : '#14ae9f';
  if (newColor === '#006666') {
    newColor = '#0fa8a8';
  }
  if (newColor === '#49308b') {
    newColor = '#815de3';
  }
  const styles = useMemo(() => pageStyles({ color: newColor }), [newColor]);
  const { profile } = props;

  const isShowActivity = useMemo(() => {
    const temp: Education[] = [...(profile?.education ?? [])];
    const found = temp.find((v) => v.description);
    return !!found;
  }, [profile?.education]);
  const isShowSummery = useMemo(() => {
    return !!(profile?.personalStatement || profile?.desiredJobTitle);
  }, [profile?.desiredJobTitle, profile?.personalStatement]);
  return (
    <Document>
      <Page size={'A4'} break={true} style={styles.body}>
        <View
          fixed
          render={({ pageNumber }) => {
            if (pageNumber !== 1) {
              return <View style={{ height: 18 }} />;
            }
            return <View />;
          }}
        />
        <View style={styles.contentWrapper}>
          {/* HEADER SECTION */}
          <View style={styles.header}>
            <Text style={styles.headerName}>{profile.name}</Text>
            <Text style={styles.headerStatement}>{profile.personalStatementHeader}</Text>
            {/* SUMMARY */}
            {isShowSummery && (
              <View style={styles.summary}>
                {profile.personalStatement && (
                  <DescriptionText
                    color="#ffffff"
                    text={profile.personalStatement}
                    fontWeight="normal"
                    lineHeight={1.2}
                    fontSize="11"
                  />
                )}
              </View>
            )}
          </View>
          <View>
            <View style={styles.contacts}>
              {profile?.mobile && (
                <ContactDetailView
                  value={profile.mobile}
                  img={`${config.frontendUrl}/phone_light.png`}
                  selectedColor={newColor}
                />
              )}
              {profile?.residentialAddress && (
                <ContactDetailView
                  value={profile.residentialAddress}
                  img={`${config.frontendUrl}/locationPin_light.png`}
                  selectedColor={newColor}
                />
              )}
              {profile?.linkedInUrl && (
                <ContactDetailView
                  isUrl={true}
                  title={'Linkedin.com'}
                  value={profile.linkedInUrl}
                  img={`${config.frontendUrl}/linkedin_light.png`}
                  selectedColor={newColor}
                />
              )}
              {profile?.userId && (
                <ContactDetailView
                  isUrl={true}
                  title={'Careers360.com'}
                  value={`${config.frontendUrl}/profile/${profile.userId}`}
                  img={`${config.frontendUrl}/careers360_light.png`}
                  selectedColor={newColor}
                />
              )}
              {profile?.email && (
                <ContactDetailView
                  title={profile.email}
                  type={'email'}
                  value={`mailto:${profile.email}`}
                  img={`${config.frontendUrl}/email_light.png`}
                  selectedColor={newColor}
                />
              )}
            </View>
          </View>

          <View style={styles.resumeContent}>
            {/* SKILLS SECTION */}
            {profile?.skills && profile.skills.length > 0 && (
              <View wrap={false} style={{ marginBottom: 15 }}>
                <TitleText title="Expertise" selectedColor={newColor} />
                <View style={styles.skillWrapper}>
                  {profile.skills.map((skill, index) => (
                    <Text key={index} style={styles.skills}>
                      {skill.value}
                    </Text>
                  ))}
                </View>
              </View>
            )}

            {/* EXPERIENCE SECTION */}
            {profile.experience && profile.experience.length > 0 && (
              <View wrap={false} style={{ marginBottom: 15 }}>
                <TitleText title="Work Experience" selectedColor={newColor} />
                {profile.experience.map((exp, index) => (
                  <ExpCard
                    key={index}
                    value={exp}
                    selectedColor={newColor}
                    isLast={profile.experience ? index === profile.experience.length - 1 : false}
                  />
                ))}
              </View>
            )}

            {/* EDUCATION SECTION */}
            {profile.education && profile.education.length > 0 && (
              <View style={{ marginBottom: 15 }}>
                <TitleText title="Education" selectedColor={newColor} />
                {profile.education.map((edu, index) => (
                  <EduCard
                    key={index}
                    value={edu}
                    selectedColor={newColor}
                    isLast={profile.education ? index === profile.education.length - 1 : false}
                  />
                ))}
              </View>
            )}

            {/* ACTIVITIES & SOCIETIES SECTION */}
            {isShowActivity && (
              <View style={{ marginBottom: 15 }}>
                <TitleText title="Achievements" selectedColor={newColor} />
                <ActivitySocityView value={profile.activities} selectedColor={newColor} />
              </View>
            )}
            {/* LANGUAGES SECTION */}
            {profile?.languages && profile.languages.length > 0 && (
              <View wrap={false} style={{ marginBottom: 15 }}>
                <TitleText title="Languages" selectedColor={newColor} />
                <View style={styles.languages}>
                  {profile.languages.map((lang, index) => (
                    <View key={index} style={{ marginTop: 10 }}>
                      <Text style={styles.designation}>{lang.language}</Text>
                      <Text style={styles.company}>{lang.fluency}</Text>
                    </View>
                  ))}
                </View>
              </View>
            )}

            {/* VOLUNTEER SECTION */}
            {profile?.volunteering && profile.volunteering.length > 0 && (
              <View style={{ marginBottom: 15 }}>
                <TitleText title="Volunteering" selectedColor={newColor} />
                {profile.volunteering.map((vol, index) => (
                  <VolCard
                    key={index}
                    value={vol}
                    selectedColor={newColor}
                    isLast={
                      profile.volunteering ? index === profile.volunteering.length - 1 : false
                    }
                  />
                ))}
              </View>
            )}

            {/* REFERENCES SECTION */}
            {profile?.reference && profile.reference.length > 0 && (
              <View wrap={false} style={{ marginBottom: 15 }}>
                <TitleText title="References" selectedColor={newColor} />
                {profile.reference.map((ref, index) => (
                  <ReferenceView
                    key={index}
                    value={ref}
                    selectedColor={newColor}
                    isLast={profile.reference ? index === profile.reference.length - 1 : false}
                  />
                ))}
              </View>
            )}
          </View>
        </View>
      </Page>
    </Document>
  );
};
export default Template2;
